import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Tooltip,
  Button,
  Popover,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  addMonths,
  addWeeks,
  isSameDay,
  isToday,
} from "date-fns";
import leaveApiService from "../../services/leave-api";
import holidayApiService from "../../services/holiday-api";
import companyCalendarEventsApiService from "../../services/company-calendar-events-api";
import { LeaveCalendarEvent, LeaveRequestStatus } from "../../types/leave";

type CalendarView = "month" | "week";

interface CalendarEvent extends Omit<
  LeaveCalendarEvent,
  "leaveTypeName" | "totalDays"
> {
  type: "leave" | "holiday" | "workday_override" | "company_event";
  leaveTypeName: string;
  totalDays: number;
  officeLocations?: string[];
  description?: string;
  startDate?: string;
  endDate?: string;
  leaveType?: string;
}

interface CalendarPageProps {
  compact?: boolean;
  fillHeight?: boolean;
}

const CalendarPage: React.FC<CalendarPageProps> = ({
  compact = false,
  fillHeight = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const todayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<CalendarView>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverEvents, setPopoverEvents] = useState<CalendarEvent[]>([]);
  const [containerHeight, setContainerHeight] = useState(0);

  // Calculate cell heights dynamically when fillHeight is enabled
  useEffect(() => {
    if (fillHeight && containerRef.current) {
      const updateHeight = () => {
        if (containerRef.current) {
          setContainerHeight(containerRef.current.clientHeight);
        }
      };
      updateHeight();
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }
  }, [fillHeight]);

  // Cell heights: dynamic when fillHeight enabled, otherwise based on compact mode
  // Header ~60px, day labels ~30px, padding ~16px = ~106px overhead in calendar
  const availableGridHeight = containerHeight - 106;
  const monthCellHeight =
    fillHeight && containerHeight > 0
      ? Math.max(60, Math.floor(availableGridHeight / 6)) // 6 rows in month view
      : compact
        ? 75
        : 120;
  const weekCellHeight =
    fillHeight && containerHeight > 0
      ? Math.max(100, availableGridHeight - 40) // Single row for week events
      : compact
        ? 120
        : 200;

  const fetchCalendarEvents = async (startDate: Date, endDate: Date) => {
    try {
      setLoading(true);
      setError(null);

      const startDateStr = format(startDate, "yyyy-MM-dd");
      const endDateStr = format(endDate, "yyyy-MM-dd");

      // Fetch leave events - only approved leaves
      const leaveData = await leaveApiService.getLeaveCalendar(
        startDateStr,
        endDateStr,
      );
      const leaveEvents: CalendarEvent[] = leaveData
        .filter((event) => event.status === LeaveRequestStatus.APPROVED)
        .map((event) => ({
          ...event,
          type: "leave" as const,
        }));

      // Fetch holiday events
      const holidayData = await holidayApiService.getHolidaysByDateRange(
        startDateStr,
        endDateStr,
      );

      // Helper function to convert holiday date (array or string) to string format
      const formatHolidayDate = (date: any): string => {
        if (Array.isArray(date)) {
          const [year, month, day] = date;
          return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        }
        return date;
      };

      const holidayEvents: CalendarEvent[] = holidayData.map((holiday) => {
        const formattedDate = formatHolidayDate(holiday.holidayDate);
        return {
          id: holiday.id || Math.random(),
          title: holiday.description || t("common.holiday"),
          employeeName: holiday.description || t("common.holiday"),
          start: formattedDate,
          end: formattedDate,
          startDate: formattedDate,
          endDate: formattedDate,
          leaveType: holiday.holidayTypeName || t("common.holiday"),
          leaveTypeName: holiday.holidayTypeName || t("common.holiday"),
          status: "APPROVED" as LeaveRequestStatus,
          color: holiday.color || "#FF9800", // Use color from backend or default orange
          totalDays: 1,
          type: "holiday" as const,
          officeLocations: holiday.officeLocations || [],
          description: holiday.description,
          reason: holiday.reason,
        };
      });

      // Fetch workday override events
      const workdayOverrideData =
        await holidayApiService.getWorkdayOverridesByDateRange(
          startDateStr,
          endDateStr,
        );
      const workdayOverrideEvents: CalendarEvent[] = workdayOverrideData.map(
        (override) => {
          const formattedDate = formatHolidayDate(override.overrideDate);
          return {
            id: override.id || Math.random(),
            title: override.reason || t("common.workdayOverride"),
            employeeName: override.reason || t("common.workdayOverride"),
            start: formattedDate,
            end: formattedDate,
            startDate: formattedDate,
            endDate: formattedDate,
            leaveType: "WORKDAY_OVERRIDE",
            leaveTypeName: "Workday Override",
            status: "APPROVED" as LeaveRequestStatus,
            color: override.color || "#2196F3", // Use color from backend or default blue
            totalDays: 1,
            type: "workday_override" as const,
            officeLocations: override.officeLocations || [],
            description: override.reason,
          };
        },
      );

      // Fetch company calendar events
      const companyEventsData =
        await companyCalendarEventsApiService.getEventsByDateRange(
          startDateStr,
          endDateStr,
        );
      const companyEvents: CalendarEvent[] = companyEventsData.map((event) => {
        return {
          id: event.id || Math.random(),
          title: event.title,
          employeeName: event.title,
          start: event.startDate,
          end: event.endDate,
          startDate: event.startDate,
          endDate: event.endDate,
          leaveType: "COMPANY_EVENT",
          leaveTypeName: "Company Event",
          status: "APPROVED" as LeaveRequestStatus,
          color: event.color || "#219cf3", // Purple for company events
          totalDays: 1,
          type: "company_event" as const,
          description: event.description,
        };
      });

      setEvents([
        ...leaveEvents,
        ...holidayEvents,
        ...workdayOverrideEvents,
        ...companyEvents,
      ]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch calendar events",
      );
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    if (view === "month") {
      // Use the full grid range (week-aligned) so overflow days from the
      // previous and next month are included in the event fetch.
      return {
        start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 }),
        end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 }),
      };
    } else {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 0 }), // Sunday start
        end: endOfWeek(currentDate, { weekStartsOn: 0 }),
      };
    }
  };

  const navigatePrevious = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, -1));
    } else {
      setCurrentDate(addWeeks(currentDate, -1));
    }
  };

  const navigateNext = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  useEffect(() => {
    const { start, end } = getDateRange();
    fetchCalendarEvents(start, end);
  }, [currentDate, view]);

  // Scroll to today in mobile view
  useEffect(() => {
    if (isMobile && todayRef.current) {
      todayRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isMobile, currentDate, events]);

  const parseEventDate = (dateValue: string | number | any[]): Date => {
    if (Array.isArray(dateValue)) {
      // Handle array format [year, month, day, hour, minute, second]
      const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
      return new Date(year, month - 1, day, hour, minute, second); // month is 0-based in JS
    } else if (typeof dateValue === "string") {
      // Handle date string format (YYYY-MM-DD or ISO format)
      if (dateValue.includes("T")) {
        // ISO format with time
        return new Date(dateValue);
      } else if (dateValue.includes("-")) {
        // Date-only format (YYYY-MM-DD) - parse as local date
        const [year, month, day] = dateValue.split("-").map(Number);
        return new Date(year, month - 1, day);
      } else {
        // Unix timestamp as string (seconds since epoch)
        const timestamp = parseInt(dateValue, 10);
        if (!isNaN(timestamp)) {
          return new Date(timestamp * 1000); // Convert seconds to milliseconds
        }
        throw new Error(`Invalid date format: ${dateValue}`);
      }
    } else if (typeof dateValue === "number") {
      if (!isNaN(dateValue)) {
        return new Date(dateValue * 1000); // Convert seconds to milliseconds
      }
      throw new Error(`Invalid date format: ${dateValue}`);
    } else {
      throw new Error("Invalid date format");
    }
  };

  // Helper function to calculate event positioning for continuous bars
  const calculateEventRows = (allDays: Date[]) => {
    const eventRows: { [key: string]: number } = {};
    const rowOccupancy: { [key: number]: Set<string> } = {};

    // Sort events by start date, then by duration (longer events first)
    const sortedEvents = [...events].sort((a, b) => {
      const startA = parseEventDate(a.start);
      const startB = parseEventDate(b.start);
      if (startA.getTime() !== startB.getTime()) {
        return startA.getTime() - startB.getTime();
      }
      // If same start date, longer events first
      const endA = parseEventDate(a.end);
      const endB = parseEventDate(b.end);
      return (
        endB.getTime() - startB.getTime() - (endA.getTime() - startA.getTime())
      );
    });

    sortedEvents.forEach((event) => {
      const eventStart = parseEventDate(event.start);
      const eventEnd = parseEventDate(event.end);

      // Find the first available row for this event
      let row = 0;
      while (true) {
        if (!rowOccupancy[row]) {
          rowOccupancy[row] = new Set();
        }

        // Check if this row has any conflicting events
        let hasConflict = false;
        for (const day of allDays) {
          if (day >= eventStart && day <= eventEnd) {
            const dayKey = format(day, "yyyy-MM-dd");
            if (rowOccupancy[row].has(dayKey)) {
              hasConflict = true;
              break;
            }
          }
        }

        if (!hasConflict) {
          // Assign this event to this row
          eventRows[event.id] = row;
          for (const day of allDays) {
            if (day >= eventStart && day <= eventEnd) {
              const dayKey = format(day, "yyyy-MM-dd");
              rowOccupancy[row].add(dayKey);
            }
          }
          break;
        }
        row++;
      }
    });

    return eventRows;
  };

  const getEventColor = (event: CalendarEvent) => {
    if (event.color) {
      return event.color;
    }
    if (event.type === "holiday") {
      return "warning"; // Orange
    } else if (event.type === "workday_override") {
      return "primary"; // Blue
    } else if (event.type === "company_event") {
      return "secondary"; // Purple for company events
    }
    return "#424242"; // Very dark gray for approved leaves (white text visible)
  };

  const getOfficeFlags = (locations: string[]): string => {
    return locations
      .map((loc) => {
        const l = loc.toLowerCase();
        if (l === "vietnam" || l === "vn") return "🇻🇳";
        if (l === "japan" || l === "jp") return "🇯🇵";
        if (l === "philippines" || l === "ph") return "🇵🇭";
        return `[${loc}]`; // fallback for unknown locations
      })
      .join(" ");
  };

  const getEventLabel = (event: CalendarEvent): string => {
    if (event.type === "holiday" || event.type === "workday_override") {
      const flags =
        event.officeLocations && event.officeLocations.length > 0
          ? getOfficeFlags(event.officeLocations) + " "
          : "";
      const description =
        event.type === "holiday"
          ? event.description || "Holiday"
          : event.description || "Workday Override";
      return `${flags}${description}`;
    }
    // For leave events and company events
    if (event.type === "leave" && event.isHalfDay && event.halfDayPeriod) {
      const period = event.halfDayPeriod === "MORNING" ? "AM" : "PM";
      return `(${period}) ${event.employeeName}`;
    }
    return event.employeeName;
  };

  const getTooltipText = (event: CalendarEvent): string => {
    if (event.type === "holiday") {
      const locations =
        event.officeLocations && event.officeLocations.length > 0
          ? `\n(Office Locations: ${event.officeLocations.join(", ")})`
          : "";
      return `${event.description || "Holiday"}${locations}`;
    } else if (event.type === "workday_override") {
      const locations =
        event.officeLocations && event.officeLocations.length > 0
          ? `\n(Office Locations: ${event.officeLocations.join(", ")})`
          : "";
      return `${event.description || "Workday Override"}${locations}`;
    } else if (event.type === "company_event") {
      return event.description
        ? `${event.title}\n${event.description}`
        : event.title;
    }
    // For leave events
    const halfDayInfo =
      event.isHalfDay && event.halfDayPeriod
        ? `(${event.halfDayPeriod === "MORNING" ? "AM" : "PM"}) `
        : "";
    return `${halfDayInfo}${event.employeeName} - ${event.leaveTypeName}`;
  };

  const renderCalendarGrid = () => {
    const { start, end } = getDateRange();
    const days = eachDayOfInterval({ start, end });

    if (view === "month") {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);

      // Mobile row-based layout - only show actual days of the month
      if (isMobile) {
        const monthDays = eachDayOfInterval({
          start: monthStart,
          end: monthEnd,
        });

        return (
          <Box
            sx={{
              maxHeight: "calc(100vh - 250px)",
              overflowY: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {monthDays.map((day) => {
              const isCurrentMonth = true; // Always true since we only show current month days
              const isCurrentDay = isToday(day);
              const dayOfWeek = day.getDay(); // 0 = Sunday, 6 = Saturday
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              const dayName = format(day, "EEE"); // Mon, Tue, etc.

              // Get events for this day
              const dayEvents = events
                .filter((event) => {
                  const eventStart = parseEventDate(event.start);
                  const eventEnd = parseEventDate(event.end);
                  // Normalize dates to midnight for accurate comparison
                  const dayMidnight = new Date(
                    day.getFullYear(),
                    day.getMonth(),
                    day.getDate(),
                  );
                  const startMidnight = new Date(
                    eventStart.getFullYear(),
                    eventStart.getMonth(),
                    eventStart.getDate(),
                  );
                  const endMidnight = new Date(
                    eventEnd.getFullYear(),
                    eventEnd.getMonth(),
                    eventEnd.getDate(),
                  );
                  return (
                    dayMidnight >= startMidnight && dayMidnight <= endMidnight
                  );
                })
                .filter((event) => {
                  // For leave events, exclude chips on holidays, weekends, and rest days
                  if (event.type === "leave") {
                    // Check if this day has a holiday (NOT company_event - those are just reminders)
                    const dayMidnight = new Date(
                      day.getFullYear(),
                      day.getMonth(),
                      day.getDate(),
                    );
                    const hasHoliday = events.some((e) => {
                      if (e.type === "holiday") {
                        const eventDate = parseEventDate(e.start);
                        const eventMidnight = new Date(
                          eventDate.getFullYear(),
                          eventDate.getMonth(),
                          eventDate.getDate(),
                        );
                        return (
                          eventMidnight.getTime() === dayMidnight.getTime()
                        );
                      }
                      return false;
                    });
                    // Removed: Show all leaves
                    // if (hasHoliday) {
                    //   return false; // Don't show leave chip on holidays
                    // }
                    // // Check if this day is a weekend
                    // if (isWeekend) {
                    //   return false; // Don't show leave chip on weekends
                    // }
                  }
                  return true;
                })
                .sort((a, b) => {
                  const priority = {
                    holiday: 0,
                    company_event: 1,
                    workday_override: 2,
                    leave: 3,
                  };
                  return priority[a.type] - priority[b.type];
                });

              return (
                <Box
                  key={day.toISOString()}
                  ref={isCurrentDay ? todayRef : null}
                  sx={{
                    display: "flex",
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 60,
                    bgcolor: (theme) => {
                      if (isCurrentDay) {
                        return theme.palette.mode === "dark"
                          ? "rgba(25, 118, 210, 0.15)"
                          : "rgba(25, 118, 210, 0.08)";
                      }
                      if (isWeekend) {
                        return theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "grey.100";
                      }
                      if (!isCurrentMonth) {
                        return theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.02)"
                          : "grey.50";
                      }
                      return theme.palette.background.paper;
                    },
                    "&:last-child": {
                      borderBottom: 0,
                    },
                  }}
                >
                  {/* Left side - Date */}
                  <Box
                    sx={{
                      width: 80,
                      minWidth: 80,
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: 1,
                      borderColor: "divider",
                      bgcolor: (theme) =>
                        isCurrentDay
                          ? theme.palette.mode === "dark"
                            ? "rgba(25, 118, 210, 0.2)"
                            : "rgba(25, 118, 210, 0.12)"
                          : "transparent",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: isCurrentDay ? "bold" : "600",
                        color: isCurrentDay
                          ? "primary.main"
                          : isWeekend
                            ? "text.secondary"
                            : isCurrentMonth
                              ? "text.primary"
                              : "text.secondary",
                        lineHeight: 1,
                      }}
                    >
                      {format(day, "d")}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isCurrentDay
                          ? "primary.main"
                          : isWeekend
                            ? "text.secondary"
                            : "text.secondary",
                        fontWeight: isCurrentDay ? "600" : "normal",
                        mt: 0.5,
                      }}
                    >
                      {dayName}
                    </Typography>
                  </Box>

                  {/* Right side - Events */}
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      overflow: "hidden",
                    }}
                  >
                    {dayEvents.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontStyle: "italic" }}
                      >
                        {/* Empty space for days with no events */}
                      </Typography>
                    ) : (
                      <>
                        {dayEvents.map((event, idx) => (
                          <Tooltip
                            key={`${event.id}-${idx}`}
                            title={getTooltipText(event)}
                          >
                            <Chip
                              label={getEventLabel(event)}
                              size="small"
                              sx={{
                                backgroundColor: getEventColor(event),
                                color: "#ffffff",
                                fontSize: "0.75rem",
                                height: "24px",
                                minHeight: "24px",
                                maxWidth: "100%",
                                "& .MuiChip-label": {
                                  px: 1,
                                  py: 0,
                                  color: "#ffffff",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "block",
                                  maxWidth: "100%",
                                },
                              }}
                            />
                          </Tooltip>
                        ))}
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      }

      // Desktop grid layout - add padding days to complete the grid (42 days = 6 weeks)
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Start on Sunday
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
      const allDays = eachDayOfInterval({
        start: calendarStart,
        end: calendarEnd,
      });

      return (
        <Box sx={{ position: "relative" }}>
          <Grid
            container
            spacing={0}
            sx={{ border: 1, borderColor: "divider" }}
          >
            {/* Week day headers */}
            {[
              t("days.Sun"),
              t("days.Mon"),
              t("days.Tue"),
              t("days.Wed"),
              t("days.Thu"),
              t("days.Fri"),
              t("days.Sat"),
            ].map((day, index) => {
              const isWeekend = index === 0 || index === 6; // Sunday or Saturday
              return (
                <Grid size={{ xs: 12 / 7 }} key={day}>
                  <Paper
                    sx={{
                      p: 1,
                      textAlign: "center",
                      bgcolor: (theme) =>
                        isWeekend
                          ? theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "grey.200"
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "grey.100",
                      borderRadius: 0,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color={isWeekend ? "text.secondary" : "text.primary"}
                    >
                      {day}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}

            {/* Calendar days */}
            {allDays.map((day) => {
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isCurrentDay = isToday(day);
              const dayOfWeek = day.getDay(); // 0 = Sunday, 6 = Saturday
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

              // Get events for this day and sort them
              // Priority: holidays, company_events, workday_override, then leaves
              const dayEvents = events
                .filter((event) => {
                  const eventStart = parseEventDate(event.start);
                  const eventEnd = parseEventDate(event.end);
                  // Normalize dates to midnight for accurate comparison
                  const dayMidnight = new Date(
                    day.getFullYear(),
                    day.getMonth(),
                    day.getDate(),
                  );
                  const startMidnight = new Date(
                    eventStart.getFullYear(),
                    eventStart.getMonth(),
                    eventStart.getDate(),
                  );
                  const endMidnight = new Date(
                    eventEnd.getFullYear(),
                    eventEnd.getMonth(),
                    eventEnd.getDate(),
                  );
                  return (
                    dayMidnight >= startMidnight && dayMidnight <= endMidnight
                  );
                })
                .filter((event) => {
                  // For leave events, exclude chips on holidays, weekends, and rest days
                  if (event.type === "leave") {
                    // Check if this day has a holiday (NOT company_event - those are just reminders)
                    const dayMidnight = new Date(
                      day.getFullYear(),
                      day.getMonth(),
                      day.getDate(),
                    );
                    const hasHoliday = events.some((e) => {
                      if (e.type === "holiday") {
                        const eventDate = parseEventDate(e.start);
                        const eventMidnight = new Date(
                          eventDate.getFullYear(),
                          eventDate.getMonth(),
                          eventDate.getDate(),
                        );
                        return (
                          eventMidnight.getTime() === dayMidnight.getTime()
                        );
                      }
                      return false;
                    });
                    // Removed: Show all leaves
                    // if (hasHoliday) {
                    //   return false; // Don't show leave chip on holidays
                    // }
                    // // Check if this day is a weekend
                    // if (isWeekend) {
                    //   return false; // Don't show leave chip on weekends
                    // }
                  }
                  return true;
                })
                .sort((a, b) => {
                  const priority = {
                    holiday: 0,
                    company_event: 1,
                    workday_override: 2,
                    leave: 3,
                  };
                  return priority[a.type] - priority[b.type];
                });

              return (
                <Grid size={{ xs: 12 / 7 }} key={day.toISOString()}>
                  <Paper
                    sx={{
                      height: monthCellHeight,
                      p: compact ? 0.5 : 1,
                      borderRadius: 0,
                      border: isCurrentDay ? 2 : 1,
                      borderColor: isCurrentDay ? "primary.main" : "divider",
                      bgcolor: (theme) => {
                        if (isCurrentDay) {
                          return theme.palette.mode === "dark"
                            ? "rgba(25, 118, 210, 0.15)"
                            : "rgba(25, 118, 210, 0.08)";
                        }
                        if (!isCurrentMonth) {
                          // Overflow days from prev/next month — clearly muted
                          return theme.palette.mode === "dark"
                            ? "rgba(0, 0, 0, 0.35)"
                            : "rgba(0, 0, 0, 0.01)";
                        }
                        if (isWeekend) {
                          return theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "grey.100";
                        }
                        return theme.palette.background.paper;
                      },
                      position: "relative",
                      boxShadow: isCurrentDay
                        ? "inset 0 0 0 1px rgba(25, 118, 210, 0.3)"
                        : "none",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isCurrentDay ? "bold" : "normal",
                        fontStyle: !isCurrentMonth ? "italic" : "normal",
                        color: (theme) =>
                          isCurrentDay
                            ? theme.palette.primary.main
                            : !isCurrentMonth
                              ? theme.palette.mode === "dark"
                                ? theme.palette.grey[700]
                                : theme.palette.grey[300]
                              : isWeekend
                                ? theme.palette.text.secondary
                                : theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {format(day, "d")}
                    </Typography>

                    {/* Render events for this day */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        flex: 1,
                        overflow: "auto",
                      }}
                    >
                      {dayEvents.slice(0, 2).map((event, idx) => (
                        <Tooltip
                          key={`${event.id}-${idx}`}
                          title={getTooltipText(event)}
                        >
                          <Chip
                            label={getEventLabel(event)}
                            size="small"
                            sx={{
                              backgroundColor: getEventColor(event),
                              color: "#ffffff",
                              fontSize: "0.65rem",
                              height: "20px",
                              minHeight: "20px",
                              maxWidth: "100%",
                              opacity: isCurrentMonth ? 1 : 0.4,
                              "& .MuiChip-label": {
                                px: 0.5,
                                py: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: "#ffffff",
                                display: "block",
                                maxWidth: "100%",
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                      {dayEvents.length > 2 && (
                        <Typography
                          variant="caption"
                          onClick={(e) => {
                            setPopoverAnchor(e.currentTarget);
                            setPopoverEvents(dayEvents);
                          }}
                          sx={{
                            fontSize: "0.65rem",
                            color: "primary.main",
                            fontWeight: "bold",
                            textAlign: "center",
                            cursor: "pointer",
                            opacity: isCurrentMonth ? 1 : 0.4,
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          +{dayEvents.length - 2} more
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    } else {
      // Week view - same style as month view
      return (
        <Box>
          <Grid
            container
            spacing={0}
            sx={{ border: 1, borderColor: "divider" }}
          >
            {/* Week day headers */}
            {days.map((day) => {
              const dayOfWeek = day.getDay(); // 0 = Sunday, 6 = Saturday
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              const isCurrentDay = isToday(day);

              return (
                <Grid size={{ xs: 12 / 7 }} key={day.toISOString()}>
                  <Paper
                    sx={{
                      p: 1,
                      textAlign: "center",
                      bgcolor: (theme) => {
                        if (isCurrentDay) {
                          return theme.palette.mode === "dark"
                            ? "rgba(25, 118, 210, 0.25)"
                            : "rgba(25, 118, 210, 0.12)";
                        }
                        if (isWeekend) {
                          return theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "grey.200";
                        }
                        return theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "grey.100";
                      },
                      borderRadius: 0,
                      borderBottom: isCurrentDay ? 3 : 1,
                      borderColor: isCurrentDay ? "primary.main" : "divider",
                      boxShadow: isCurrentDay
                        ? "0 2px 8px rgba(25, 118, 210, 0.3)"
                        : "none",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      color={
                        isCurrentDay
                          ? "primary.main"
                          : isWeekend
                            ? "text.secondary"
                            : "text.primary"
                      }
                    >
                      {format(day, "EEE")}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={isCurrentDay ? 900 : 600}
                      color={
                        isCurrentDay
                          ? "primary.main"
                          : isWeekend
                            ? "text.secondary"
                            : "text.primary"
                      }
                    >
                      {format(day, "d")}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}

            {/* Week event area - same as month view */}
            {days.map((day) => {
              const dayOfWeek = day.getDay(); // 0 = Sunday, 6 = Saturday
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              const isCurrentDay = isSameDay(day, new Date());

              // Get events for this day and sort them
              // Priority: holidays, company_events, workday_override, then leaves
              const dayEvents = events
                .filter((event) => {
                  const eventStart = parseEventDate(event.start);
                  const eventEnd = parseEventDate(event.end);
                  // Normalize dates to midnight for accurate comparison
                  const dayMidnight = new Date(
                    day.getFullYear(),
                    day.getMonth(),
                    day.getDate(),
                  );
                  const startMidnight = new Date(
                    eventStart.getFullYear(),
                    eventStart.getMonth(),
                    eventStart.getDate(),
                  );
                  const endMidnight = new Date(
                    eventEnd.getFullYear(),
                    eventEnd.getMonth(),
                    eventEnd.getDate(),
                  );
                  return (
                    dayMidnight >= startMidnight && dayMidnight <= endMidnight
                  );
                })
                .filter((event) => {
                  // For leave events, exclude chips on holidays, weekends, and rest days
                  if (event.type === "leave") {
                    // Check if this day has a holiday (NOT company_event - those are just reminders)
                    const dayMidnight = new Date(
                      day.getFullYear(),
                      day.getMonth(),
                      day.getDate(),
                    );
                    const hasHoliday = events.some((e) => {
                      if (e.type === "holiday") {
                        const eventDate = parseEventDate(e.start);
                        const eventMidnight = new Date(
                          eventDate.getFullYear(),
                          eventDate.getMonth(),
                          eventDate.getDate(),
                        );
                        return (
                          eventMidnight.getTime() === dayMidnight.getTime()
                        );
                      }
                      return false;
                    });
                    // Removed: Show all leaves
                    // if (hasHoliday) {
                    //   return false; // Don't show leave chip on holidays
                    // }
                    // // Check if this day is a weekend
                    // if (isWeekend) {
                    //   return false; // Don't show leave chip on weekends
                    // }
                  }
                  return true;
                })
                .sort((a, b) => {
                  const priority = {
                    holiday: 0,
                    company_event: 1,
                    workday_override: 2,
                    leave: 3,
                  };
                  return priority[a.type] - priority[b.type];
                });

              return (
                <Grid size={{ xs: 12 / 7 }} key={`events-${day.toISOString()}`}>
                  <Paper
                    sx={{
                      height: weekCellHeight,
                      p: compact ? 0.5 : 1,
                      borderRadius: 0,
                      border: isCurrentDay ? 2 : 1,
                      borderColor: isCurrentDay ? "primary.main" : "divider",
                      bgcolor: (theme) => {
                        if (isCurrentDay) {
                          return theme.palette.mode === "dark"
                            ? "rgba(25, 118, 210, 0.15)"
                            : "rgba(25, 118, 210, 0.08)";
                        }
                        return isWeekend
                          ? theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "grey.100"
                          : theme.palette.background.paper;
                      },
                      boxShadow: isCurrentDay
                        ? "inset 0 0 0 1px rgba(25, 118, 210, 0.3)"
                        : "none",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Render events for this day */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        flex: 1,
                        overflow: "hidden",
                      }}
                    >
                      {dayEvents.slice(0, 7).map((event) => (
                        <Tooltip key={event.id} title={getTooltipText(event)}>
                          <Chip
                            label={getEventLabel(event)}
                            size="small"
                            sx={{
                              backgroundColor: getEventColor(event),
                              color: "#ffffff",
                              fontSize: "0.7rem",
                              height:
                                event.type === "holiday" ||
                                event.type === "workday_override"
                                  ? "auto"
                                  : "22px",
                              minHeight: "22px",
                              "& .MuiChip-label": {
                                px: 0.5,
                                py:
                                  event.type === "holiday" ||
                                  event.type === "workday_override"
                                    ? 0.5
                                    : 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace:
                                  event.type === "holiday" ||
                                  event.type === "workday_override"
                                    ? "normal"
                                    : "nowrap",
                                color: "#ffffff",
                                wordBreak: "break-word",
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                      {dayEvents.length > 7 && (
                        <Typography
                          variant="caption"
                          onClick={(e) => {
                            setPopoverAnchor(e.currentTarget);
                            setPopoverEvents(dayEvents);
                          }}
                          sx={{
                            fontSize: "0.7rem",
                            color: "primary.main",
                            fontWeight: "bold",
                            textAlign: "center",
                            cursor: "pointer",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          +{dayEvents.length - 7} more
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={() => {
              const { start, end } = getDateRange();
              fetchCalendarEvents(start, end);
            }}
          >
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  const { start, end } = getDateRange();
  const dateRangeText =
    view === "month"
      ? isMobile
        ? format(currentDate, "yyyy/MM")
        : format(currentDate, "MMMM yyyy")
      : `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`;

  return (
    <Box
      ref={containerRef}
      sx={{
        pt: 0,
        pr: 2,
        pb: fillHeight ? 0 : 2,
        pl: 2,
        height: fillHeight ? "100%" : "auto",
        display: fillHeight ? "flex" : "block",
        flexDirection: "column",
      }}
    >
      {/* Calendar Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: fillHeight ? 1 : 3,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
          flexShrink: 0,
        }}
      >
        {/* View Toggle - Hidden in mobile */}
        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, newView) => newView && setView(newView)}
              size="small"
            >
              <ToggleButton value="month">
                <CalendarMonthIcon sx={{ mr: 1 }} />
                {t("timetracking.filters.month")}
              </ToggleButton>
              <ToggleButton value="week">
                <CalendarViewWeekIcon sx={{ mr: 1 }} />
                {t("timetracking.filters.week")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Navigation Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1,
            width: isMobile ? "100%" : "auto",
            justifyContent: isMobile ? "space-between" : "flex-end",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={navigateToday}
            sx={{
              minWidth: isMobile ? "auto" : undefined,
              px: isMobile ? 1.5 : 2,
            }}
          >
            {t("timetracking.filters.today")}
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={navigatePrevious}
              size={isMobile ? "small" : "medium"}
            >
              <ChevronLeftIcon />
            </IconButton>

            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{
                minWidth: isMobile ? 80 : 200,
                textAlign: "center",
                fontWeight: isMobile ? 600 : 400,
              }}
            >
              {dateRangeText}
            </Typography>

            <IconButton
              onClick={navigateNext}
              size={isMobile ? "small" : "medium"}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <IconButton
            onClick={() => {
              const { start, end } = getDateRange();
              fetchCalendarEvents(start, end);
            }}
            size={isMobile ? "small" : "medium"}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          flex: fillHeight ? 1 : "0 0 auto",
          minHeight: 0,
          overflow: fillHeight ? "hidden" : "visible",
        }}
      >
        {renderCalendarGrid()}
      </Box>

      {/* Popover for showing all events */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2, maxWidth: 400 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            All Events
          </Typography>
          <List dense>
            {popoverEvents.map((event) => (
              <ListItem key={event.id} sx={{ px: 0 }}>
                <Chip
                  label={getEventLabel(event)}
                  size="small"
                  sx={{
                    backgroundColor: getEventColor(event),
                    color: "#ffffff",
                    fontSize: "0.75rem",
                    mr: 1,
                    minWidth: 100,
                    height:
                      event.type === "holiday" ||
                      event.type === "workday_override"
                        ? "auto"
                        : "24px",
                    minHeight: "24px",
                    "& .MuiChip-label": {
                      color: "#ffffff",
                      py:
                        event.type === "holiday" ||
                        event.type === "workday_override"
                          ? 0.5
                          : 0,
                      whiteSpace:
                        event.type === "holiday" ||
                        event.type === "workday_override"
                          ? "normal"
                          : "nowrap",
                      wordBreak: "break-word",
                    },
                  }}
                />
                <ListItemText
                  primary={getTooltipText(event)}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontSize: "0.85rem" },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};

export default CalendarPage;
